<template>
    <Layout>
        <div v-if="error" class="flex flex-col items-center justify-center space-y-8 p-32">
            <p class="text-xl font-bold">
                {{ $t('This job is no longer available.') }}
            </p>
            <p>
                <InertiaLink :href="$page.props.locale == 'fr' ? `/emplois` : `/en/job-opportunities`" class="button">
                    {{ $t('See other job opportunities') }}
                </InertiaLink>
            </p>
        </div>

        <div v-else>
            <!-- Page heading -->
            <div>
                <img class="h-48 w-full object-cover lg:h-64" :src="offer.banner" alt="" />

                <div class="mx-auto -mt-16 max-w-6xl">
                    <!-- Logo & title -->
                    <div class="flex">
                        <a
                            :href="`https://${offer.company.subdomain}.camaq.ca`"
                            class="z-10 hidden h-36 w-48 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-gray-300 bg-white md:flex"
                        >
                            <img :src="offer.company.logo" alt="" class="h-32 w-32 object-contain" />
                        </a>

                        <div class="-ml-10 flex flex-1 items-center">
                            <div
                                class="flex flex-1 flex-col items-center justify-between space-y-6 rounded-lg border bg-white p-6 pl-16 shadow-lg md:flex-row md:space-y-0"
                            >
                                <div class="max-w-[300px] overflow-hidden text-ellipsis sm:max-w-[550px] md:max-w-[450px] lg:max-w-[600px]">
                                    <h1 class="line-clamp-2 text-3xl font-bold text-gray-900" :title="offer.title">
                                        {{ offer.title }}
                                    </h1>

                                    <InertiaLink
                                        v-if="!offer.is_anonymous"
                                        :href="
                                            $page.props.locale == 'fr'
                                                ? `/emplois?entreprise=${offer.company.slug}`
                                                : `/job-opportunities?entreprise=${offer.company.slug}`
                                        "
                                        class="text-sm font-medium underline"
                                    >
                                        {{
                                            $t('View all :count offers for :company', {
                                                count: offer.company.offers_count,
                                                company: offer.company.name,
                                            })
                                        }}
                                    </InertiaLink>
                                </div>

                                <div class="flex items-center space-x-4">
                                    <p v-if="alreadyApplied" class="text-sm font-medium text-green-900">
                                        {{ $t('You have already applied on this offer.') }}
                                    </p>

                                    <button v-else class="button" @click.prevent="applicationModalRef.show()">
                                        {{ $t('Apply now') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Info cards (offer information, salary, address, etc) -->
                    <div class="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <div
                            v-if="offer.city && offer.city.length > 0 && offer.city != 'Non spécifié' && offer.city != 'Not specified'"
                            class="badge"
                            :title="$t('City')"
                        >
                            <IconMapPin :size="18" />
                            <span>{{ addressInfo }}</span>
                        </div>

                        <div v-if="offer.availability" class="badge" :title="$t('Availability')">
                            <IconClock24 :size="18" />
                            <span>{{ offer.availability }}</span>
                        </div>

                        <div v-if="offer.weekly_hours" class="badge" :title="$t('Hours per week')">
                            <IconCalendarWeek :size="18" />
                            <span v-text="hoursPerWeek(offer.weekly_hours)"></span>
                        </div>

                        <div v-if="offer.number_openings && offer.number_openings > 0" class="badge" :title="$t('Number of openings')">
                            <IconUsersGroup :size="18" />
                            <span>{{ offer.number_openings }}</span>
                        </div>

                        <div v-if="offer.salary" class="badge" :title="$t('Salary')">
                            <IconCurrencyDollar :size="18" />
                            <span>{{ offer.salary }}</span>
                        </div>

                        <div v-if="offer.work_shift" class="badge" :title="$t('Work shift')">
                            <IconSunMoon :size="18" />
                            <span>{{ offer.work_shift }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- New text fields (scraping offers only for now) -->
            <!-- <div
                v-if="!offer.is_protected && offer.text_fields?.job_description?.length"
                class="prose mx-auto mt-8 max-w-xl space-y-8 px-8 lg:prose-lg lg:max-w-3xl xl:max-w-5xl"
            >
                <div v-text="offer.text_fields.job_description"></div>

                <div v-if="offer.text_fields?.responsibilities?.length" class="space-y-4">
                    <p class="font-bold">
                        {{ locale == 'fr' ? 'Responsabilités' : 'Responsibilities' }}
                    </p>
                    <div v-html="offer.text_fields.responsibilities"></div>
                </div>

                <div v-if="offer.text_fields?.benefits?.length" class="space-y-4">
                    <p class="font-bold">
                        {{ locale == 'fr' ? 'Bénéfices' : 'Benefits' }}
                    </p>
                    <div v-html="offer.text_fields.benefits"></div>
                </div>

                <div v-if="offer.text_fields?.requirements?.length" class="space-y-4">
                    <p class="font-bold">
                        {{ locale == 'fr' ? 'Compétences requises' : 'Requirements' }}
                    </p>
                    <div v-html="offer.text_fields.requirements"></div>
                </div>
            </div> -->

            <!-- Full HTML Description -->
            <div class="prose mx-auto mt-8 max-w-xl px-8 lg:prose-lg lg:max-w-3xl xl:max-w-5xl">
                <div v-html="description"></div>
            </div>

            <!-- Need help -->
            <div class="flex flex-col items-center justify-center space-y-4 py-16">
                <p class="text-xl font-semibold">
                    {{ $t('Need assistance?') }}
                </p>
                <p class="underline">
                    <a href="mailto:support@camaq.ca">support@camaq.ca</a>
                </p>
                <p class="underline">
                    <a href="tel:1-450-403-4300">1-450-403-4300</a>
                </p>
            </div>

            <!-- Sticky application bar -->
            <div
                class="fixed inset-x-0 bottom-0 flex flex-col items-center justify-center space-y-4 bg-white p-4 shadow md:flex-row md:space-x-8 md:space-y-0"
            >
                <p class="font-medium">{{ $t('Interested by this job opportunity?') }}</p>

                <p v-if="alreadyApplied" class="text-sm font-medium text-green-900">{{ $t('You have already applied on this offer.') }}</p>

                <button v-else class="button" @click.prevent="applicationModalRef.show()">
                    {{ $t('Apply now') }}
                </button>
            </div>

            <ApplicationModal ref="applicationModalRef" :offer="offer" @success="saveApplication" />
        </div>
    </Layout>
</template>

<script setup>
    import ApplicationModal from '@/Partials/ApplicationModal';
    import { usePage } from '@inertiajs/inertia-vue3';
    import { IconCalendarWeek, IconClock24, IconCurrencyDollar, IconMapPin, IconSunMoon, IconUsersGroup } from '@tabler/icons-vue';
    import { trans } from 'laravel-vue-i18n';
    import { computed, onBeforeMount, onMounted, ref } from 'vue';
    import { useCookieConsent } from '../../useCookieConsent';

    const { consent, replaceYoutubeIframes } = useCookieConsent();

    const props = defineProps({
        error: Boolean,
        offer: Object,
    });

    const description = ref(props.offer?.description);

    const applicationModalRef = ref(null);
    const alreadyApplied = ref(false);

    const locale = computed(() => {
        return usePage().props.value.locale;
    });

    const addressInfo = computed(() => {
        if (props.offer.address == props.offer.city) {
            return props.offer.city;
        }

        if (props.offer.address == null || props.offer.address == '') {
            return props.offer.city;
        }

        return `${props.offer.address}, ${props.offer.city}`;
    });

    onBeforeMount(() => {
        if (consent != 'accept-all') {
            description.value = replaceYoutubeIframes(props.offer?.description);
        }
    });

    onMounted(() => {
        if (usePage().props.value.env != 'local') {
            checkExistingApplication();
        }

        if (!alreadyApplied.value && window.location.hash === '#application') {
            applicationModalRef.value.show();
        }
    });

    function hoursPerWeek(weeklyHours) {
        if (typeof weeklyHours === 'number') {
            return `${weeklyHours} ${trans('hours per week')}`;
        }

        return weeklyHours;
    }

    function checkExistingApplication() {
        alreadyApplied.value = getApplications().includes(props.offer.id);
    }

    function getApplications() {
        let currentApplications = localStorage.getItem('applications');
        if (currentApplications) {
            currentApplications = JSON.parse(currentApplications);
            return currentApplications;
        } else {
            return [];
        }
    }

    function saveApplication() {
        let currentApplications = localStorage.getItem('applications');
        if (currentApplications) {
            currentApplications = JSON.parse(currentApplications);
            currentApplications.push(props.offer.id);
            localStorage.setItem('applications', JSON.stringify(currentApplications));
        } else {
            localStorage.setItem('applications', JSON.stringify([props.offer.id]));
        }

        alreadyApplied.value = true;
    }
</script>
